import { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import moment from 'moment';
import { PlusIcon, FolderPlusIcon } from '@heroicons/react/24/outline';
import { formatTimeFromMinutes } from './types';
import { TimelineGrid } from './TimelineGrid';
import { DateNavigation } from './DateNavigation';
import { Button } from '../Button';
import Modal from '../Shell/Modal';
import { Label, TextField } from '../Form/tailwindForm';
import { AssetMultiSelect } from '../ui/asset-multi-select';
import useAsync from '../../hooks/useAsync';
import { useNavigate } from 'react-router-dom';

import useModal from '../../hooks/useModal';
import {
	getReservableAssets,
	getNonReservableAssets,
	setAssetReservable,
	getAssetGroups,
	createAssetGroup,
	updateAssetGroup,
	deleteAssetGroup,
	addAssetToGroup,
	removeAssetFromGroup,
} from '../../api/assets';
import {
	getReservations,
	createReservation,
	deleteReservation,
	removeAssetFromReservation,
	confirmReservation,
} from '../../api/reservations';
import { getTeam } from '../../api/company';
import { getUserFullName, getStandardAssetName } from '../helpers/functions';

// Transform API reservation to slot format for a specific asset
const reservationToSlot = (reservation, isGroupBlocked = false) => {
	// Parse times - moment parses ISO strings as UTC and converts to local for display
	const startMoment = moment(reservation.startTime);
	const endMoment = moment(reservation.endTime);
	const durationMinutes = endMoment.diff(startMoment, 'minutes');

	// Get booked by name from users
	const bookedBy =
		reservation.users && reservation.users.length > 0
			? reservation.users.map((u) => getUserFullName(u)).join(', ')
			: 'Reserved';

	// Format dates in local timezone for display
	const startDateStr = startMoment.format('YYYY-MM-DD');
	const endDateStr = endMoment.format('YYYY-MM-DD');

	// Debug: log reservation times
	console.log('Reservation times:', {
		rawStart: reservation.startTime,
		rawEnd: reservation.endTime,
		localStart: startMoment.format('YYYY-MM-DD HH:mm'),
		localEnd: endMoment.format('YYYY-MM-DD HH:mm'),
		startDateStr,
		endDateStr,
		durationMinutes,
	});

	return {
		id: reservation.id,
		reservationId: reservation.id,
		startHour: startMoment.hour(),
		startMinutes: startMoment.minute(),
		durationMinutes,
		isBooked: true,
		bookedBy,
		startDate: startDateStr,
		endDate: startDateStr !== endDateStr ? endDateStr : undefined,
		// Track if this slot is from a group reservation
		assetGroupId: reservation.assetGroupId || null,
		isGroupBlocked, // true if this is a sub-asset blocked by a group reservation
		// Status for color coding
		status: reservation.status || 'pending',
		endTime: reservation.endTime,
		// Keep reference to original reservation data
		_reservation: reservation,
	};
};

// Transform API assets to timeline format with slots from reservations
const transformAssetsWithReservations = (apiAssets, reservations) => {
	if (!apiAssets || !Array.isArray(apiAssets)) return [];

	// Create a map of asset ID to its reservations/slots
	const assetSlotsMap = {};

	if (reservations && Array.isArray(reservations)) {
		reservations.forEach((reservation) => {
			if (reservation.assets && Array.isArray(reservation.assets)) {
				reservation.assets.forEach((asset) => {
					if (!assetSlotsMap[asset.id]) {
						assetSlotsMap[asset.id] = [];
					}
					assetSlotsMap[asset.id].push(reservationToSlot(reservation));
				});
			}
		});
	}

	// Transform assets to timeline format
	return apiAssets.map((asset) => ({
		id: asset.id,
		name: getStandardAssetName(asset),
		description: asset.serialNumber || asset.location || '',
		icon: 'monitor', // Default icon, could be mapped from asset type/category
		slots: assetSlotsMap[asset.id] || [],
		// Store original asset data for reference
		_asset: asset,
	}));
};

export function AssetBookingManager({ onBookingChange }) {
	const navigate = useNavigate();

	// API data fetching - only reservable assets
	const { value: assetsData, execute: refreshAssets } = useAsync(
		getReservableAssets,
		{ immediate: false },
	);

	// Non-reservable assets for the "Add Asset" modal
	const {
		value: nonReservableAssetsData,
		execute: refreshNonReservableAssets,
	} = useAsync(getNonReservableAssets, { immediate: false });

	const { execute: onSetAssetReservable } = useAsync(setAssetReservable, {
		immediate: false,
	});

	// Asset groups
	const { value: assetGroupsData, execute: refreshAssetGroups } = useAsync(
		getAssetGroups,
		{ immediate: false },
	);

	const { execute: onCreateAssetGroup } = useAsync(createAssetGroup, {
		immediate: false,
	});

	const { execute: onDeleteAssetGroup } = useAsync(deleteAssetGroup, {
		immediate: false,
	});

	const { execute: onUpdateAssetGroup } = useAsync(updateAssetGroup, {
		immediate: false,
	});

	const { execute: onAddAssetToGroup } = useAsync(addAssetToGroup, {
		immediate: false,
	});

	const { execute: onRemoveAssetFromGroup } = useAsync(removeAssetFromGroup, {
		immediate: false,
	});

	const { value: reservationsData, execute: refreshReservations } = useAsync(
		getReservations,
		{ immediate: false },
	);

	const { value: teamData, execute: refreshTeam } = useAsync(getTeam, {
		immediate: false,
	});

	const { execute: onCreateReservation } = useAsync(createReservation, {
		immediate: false,
	});

	const { execute: onDeleteReservation } = useAsync(deleteReservation, {
		immediate: false,
	});

	const { execute: onRemoveAssetFromReservation } = useAsync(
		removeAssetFromReservation,
		{ immediate: false },
	);

	const { execute: onConfirmReservation } = useAsync(confirmReservation, {
		immediate: false,
	});

	// Modal hook for add booking modal
	const { open: addBookingModalState, setOpen: openAddBooking } =
		useModal('addBooking');

	// Track previous modal state to detect close transitions
	const prevModalState = useRef(addBookingModalState);

	// Load data on mount
	useEffect(() => {
		refreshAssets();
		refreshAssetGroups();
		refreshReservations({});
		refreshTeam();
	}, []);

	// Refresh reservations when the add booking modal closes
	// This ensures newly created/edited bookings appear without a page refresh
	useEffect(() => {
		// Only refresh when modal transitions from open (truthy) to closed (falsy)
		if (prevModalState.current && !addBookingModalState) {
			refreshReservations({});
		}
		prevModalState.current = addBookingModalState;
	}, [addBookingModalState]);

	// Transform API data to timeline format
	// Combine individual assets and asset groups for timeline display
	const assets = useMemo(() => {
		const individualAssets = transformAssetsWithReservations(
			assetsData,
			reservationsData,
		);

		// Transform asset groups to timeline format with subAssets
		const groups = [];
		if (assetGroupsData && Array.isArray(assetGroupsData)) {
			assetGroupsData.forEach((group) => {
				// Find reservations that belong to this group
				// Use string comparison to handle potential type mismatches (UUID strings)
				const groupReservations = (reservationsData || []).filter(
					(r) => r.assetGroupId && String(r.assetGroupId) === String(group.id),
				);

				// Create slots for the group row from group reservations
				const groupSlots = groupReservations.map((r) =>
					reservationToSlot(r, false),
				);

				// Transform group's assets to sub-assets format
				// Mark slots as isGroupBlocked if they come from THIS group's reservation
				const subAssets = (group.assets || []).map((asset) => {
					// Build slots for this sub-asset
					const assetSlots = [];
					if (reservationsData && Array.isArray(reservationsData)) {
						reservationsData.forEach((reservation) => {
							if (
								reservation.assets &&
								reservation.assets.some((a) => a.id === asset.id)
							) {
								// If this reservation belongs to THIS group, mark it as blocked (gray)
								// Sub-assets show group reservations as "blocked" since the main booking
								// is shown on the group row
								const isBlocked =
									reservation.assetGroupId &&
									String(reservation.assetGroupId) === String(group.id);
								assetSlots.push(reservationToSlot(reservation, isBlocked));
							}
						});
					}

					return {
						id: asset.id,
						name: getStandardAssetName(asset),
						description: asset.serialNumber || asset.location || '',
						icon: 'monitor',
						slots: assetSlots,
						_asset: asset,
					};
				});

				groups.push({
					id: `group-${group.id}`,
					groupId: group.id,
					name: group.name,
					description: group.description || `${subAssets.length} assets`,
					icon: group.icon || 'users',
					isGroup: true,
					subAssets,
					slots: groupSlots, // Group row shows the group reservation
					_group: group,
				});
			});
		}

		// Filter out individual assets that are part of a group
		const groupedAssetIds = new Set();
		if (assetGroupsData && Array.isArray(assetGroupsData)) {
			assetGroupsData.forEach((group) => {
				(group.assets || []).forEach((asset) => {
					groupedAssetIds.add(asset.id);
				});
			});
		}

		const ungroupedAssets = individualAssets.filter(
			(asset) => !groupedAssetIds.has(asset.id),
		);

		// Sort groups alphabetically by name
		const sortedGroups = [...groups].sort((a, b) =>
			(a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
		);

		// Sort ungrouped assets alphabetically by name
		const sortedUngroupedAssets = [...ungroupedAssets].sort((a, b) =>
			(a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
		);

		// Return groups first (sorted), then ungrouped individual assets (sorted)
		return [...sortedGroups, ...sortedUngroupedAssets];
	}, [assetsData, assetGroupsData, reservationsData]);
	const [searchQuery] = useState('');
	const [expandedGroups, setExpandedGroups] = useState({});
	const [selectedDate, setSelectedDate] = useState(new Date());

	// Expand all groups by default when asset groups data loads
	useEffect(() => {
		if (assetGroupsData && Array.isArray(assetGroupsData)) {
			const expanded = {};
			assetGroupsData.forEach((group) => {
				expanded[`group-${group.id}`] = true;
			});
			setExpandedGroups(expanded);
		}
	}, [assetGroupsData]);
	const [viewMode, setViewMode] = useState('day');
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Add asset modal state
	const [addAssetDialog, setAddAssetDialog] = useState({
		open: false,
		selectedAssetIds: [],
	});

	// Remove asset confirmation dialog state
	const [removeAssetDialog, setRemoveAssetDialog] = useState({
		open: false,
		assetId: '',
		assetName: '',
	});

	// Create group dialog state
	const [createGroupDialog, setCreateGroupDialog] = useState({
		open: false,
		name: '',
		description: '',
		selectedAssetIds: [],
	});

	// Delete group confirmation dialog state
	const [deleteGroupDialog, setDeleteGroupDialog] = useState({
		open: false,
		groupId: '',
		groupName: '',
	});

	// Edit group dialog state
	const [editGroupDialog, setEditGroupDialog] = useState({
		open: false,
		groupId: '',
		name: '',
		description: '',
		currentAssetIds: [], // Assets currently in the group
		selectedAssetIds: [], // Assets selected in the dialog (to be in the group)
	});

	// Zoom state for day view
	const ZOOM_LEVELS = [1200, 1800, 2400, 3600, 4800];
	const [zoomIndex, setZoomIndex] = useState(4); // Default to 2400px

	const zoomIn = () =>
		setZoomIndex((prev) => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
	const zoomOut = () => setZoomIndex((prev) => Math.max(prev - 1, 0));
	const canZoomIn = zoomIndex < ZOOM_LEVELS.length - 1;
	const canZoomOut = zoomIndex > 0;
	const zoomLevel = ZOOM_LEVELS[zoomIndex];

	// New booking dialog state
	const [newBookingDialog, setNewBookingDialog] = useState({
		open: false,
		assetId: '',
		assetName: '',
		groupId: null, // If booking a group, this will be set
		isGroup: false,
		startMinutes: 0,
		endMinutes: 0,
		date: new Date(),
		selectedUserIds: [],
	});

	// Release confirmation dialog state
	const [releaseDialog, setReleaseDialog] = useState({
		open: false,
		slotId: '',
		assetId: '', // The asset to remove (for group blocked slots)
		assetName: '',
		bookedBy: '',
		isGroupBlocked: false, // If true, remove asset from reservation instead of deleting
	});

	// Show confirmation dialog before releasing
	const handleSlotRelease = (assetId, slotId) => {
		// Find the asset and slot to get display info
		// Check both top-level assets and subAssets within groups
		let asset = assets.find((a) => a.id === assetId);
		let slot = asset?.slots?.find((s) => s.id === slotId);

		// If not found at top level, search in subAssets
		if (!slot) {
			for (const a of assets) {
				if (a.subAssets) {
					const subAsset = a.subAssets.find((sa) => sa.id === assetId);
					if (subAsset) {
						asset = subAsset;
						slot = subAsset.slots?.find((s) => s.id === slotId);
						break;
					}
				}
			}
		}

		console.log('RELEASE:', slotId, asset?.id, assetId, slot);

		setReleaseDialog({
			open: true,
			slotId,
			assetId: asset?.id || assetId,
			assetName: asset?.name || 'Unknown Asset',
			bookedBy: slot?.bookedBy || 'Reserved',
			isGroupBlocked: slot?.isGroupBlocked || false,
		});
	};

	// Actually delete the reservation or remove asset after confirmation
	const confirmRelease = async () => {
		if (isSubmitting) return;

		try {
			setIsSubmitting(true);

			if (releaseDialog.isGroupBlocked) {
				// Remove just this asset from the group reservation
				await onRemoveAssetFromReservation({
					reservationId: releaseDialog.slotId,
					assetId: releaseDialog.assetId,
				});
			} else {
				// Delete the entire reservation
				await onDeleteReservation({ id: releaseDialog.slotId });
			}

			// Close dialog and refresh data
			setReleaseDialog({
				open: false,
				slotId: '',
				assetId: '',
				assetName: '',
				bookedBy: '',
				isGroupBlocked: false,
			});
			await refreshReservations({});
			if (onBookingChange) onBookingChange();
		} catch (error) {
			console.error('Failed to release booking:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Edit a booking - open the AddBookingModal with the reservation data
	const handleSlotEdit = (_assetId, slot) => {
		if (slot._reservation) {
			// Open the existing AddBookingModal in edit mode
			openAddBooking({ id: slot._reservation.id, edit: true });
		}
	};

	// View a reservation - open the AddBookingModal in view mode
	const handleSlotView = (reservationId) => {
		if (reservationId) {
			navigate(`/bookings/${reservationId}`);
		}
	};

	// Confirm a reservation
	const handleSlotConfirm = async (reservationId) => {
		if (!reservationId || isSubmitting) return;

		try {
			setIsSubmitting(true);
			await onConfirmReservation({ id: reservationId });
			await refreshReservations({});
			if (onBookingChange) onBookingChange();
		} catch (error) {
			console.error('Failed to confirm reservation:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleToggleGroup = (assetId) => {
		setExpandedGroups((prev) => ({ ...prev, [assetId]: !prev[assetId] }));
	};

	// Handle clicking on empty area to create a new booking
	const handleEmptyAreaClick = (assetId, startMinutes, endMinutes, date) => {
		const asset = assets.find((a) => a.id === assetId);

		// Determine which asset IDs to pre-select
		let assetIdsToSelect = [];
		if (asset?.isGroup && asset?.subAssets) {
			// For groups, pre-select all sub-assets
			assetIdsToSelect = asset.subAssets.map((sa) => sa.id);
		} else if (assetId && !asset?.isGroup) {
			// For individual assets, pre-select just this asset
			assetIdsToSelect = [assetId];
		}

		// Open the AddBookingModal slider with pre-filled data
		openAddBooking({
			prefill: {
				assetIds: assetIdsToSelect,
				groupId: asset?.isGroup ? asset.groupId : null,
				startMinutes,
				endMinutes,
				date,
			},
		});
	};

	// Create a new booking via API
	const handleCreateBooking = async () => {
		if (isSubmitting) return;

		const {
			assetId,
			groupId,
			isGroup,
			startMinutes,
			endMinutes,
			date,
			selectedUserIds,
		} = newBookingDialog;

		// Build start and end time from date + minutes
		const startTime = moment(date)
			.startOf('day')
			.add(startMinutes, 'minutes')
			.toISOString();
		const endTime = moment(date)
			.startOf('day')
			.add(endMinutes, 'minutes')
			.toISOString();

		try {
			setIsSubmitting(true);

			// Build reservation payload - either for a group or individual asset
			const reservationPayload = {
				startTime,
				endTime,
				userIds: selectedUserIds.length > 0 ? selectedUserIds : [],
			};

			if (isGroup && groupId) {
				// Booking a group - pass groupId, backend will expand to all assets
				reservationPayload.groupId = groupId;
				reservationPayload.assetIds = [];
			} else {
				// Booking individual asset
				reservationPayload.assetIds = [assetId];
			}

			await onCreateReservation(reservationPayload);

			// Close dialog and refresh data
			setNewBookingDialog({
				open: false,
				assetId: '',
				assetName: '',
				groupId: null,
				isGroup: false,
				startMinutes: 0,
				endMinutes: 0,
				date: new Date(),
				selectedUserIds: [],
			});

			await refreshReservations({});
			if (onBookingChange) onBookingChange();
		} catch (error) {
			console.error('Failed to create booking:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Filter assets based on search query
	const filteredAssets = assets.filter((asset) => {
		const allRelatedAssets = [asset, ...(asset.subAssets || [])];
		const matchesSearch = allRelatedAssets.some(
			(a) =>
				a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
				(a.description || '').toLowerCase().includes(searchQuery.toLowerCase()),
		);
		return matchesSearch;
	});

	// Toggle user selection in new booking dialog
	const toggleUserSelection = (userId) => {
		setNewBookingDialog((prev) => ({
			...prev,
			selectedUserIds: prev.selectedUserIds.includes(userId)
				? prev.selectedUserIds.filter((id) => id !== userId)
				: [...prev.selectedUserIds, userId],
		}));
	};

	// Open add asset modal
	const handleOpenAddAssetDialog = () => {
		refreshNonReservableAssets();
		setAddAssetDialog({
			open: true,
			selectedAssetIds: [],
		});
	};

	// Add selected assets to reservable list
	const handleAddAssetsToReservable = async () => {
		if (isSubmitting || addAssetDialog.selectedAssetIds.length === 0) return;

		try {
			setIsSubmitting(true);
			// Add each selected asset to the reservable list
			for (const assetId of addAssetDialog.selectedAssetIds) {
				await onSetAssetReservable({ assetId, isReservable: true });
			}
			// Close dialog and refresh data
			setAddAssetDialog({
				open: false,
				selectedAssetIds: [],
			});
			await refreshAssets();
		} catch (error) {
			console.error('Failed to add assets to reservable list:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Show confirmation dialog before removing asset from reservable list
	const handleRemoveAsset = (assetId) => {
		const asset = assets.find((a) => a.id === assetId);

		// Check if this is a group (id starts with 'group-')
		if (asset?.isGroup && asset?.groupId) {
			handleDeleteGroup(asset.groupId);
			return;
		}

		setRemoveAssetDialog({
			open: true,
			assetId,
			assetName: asset?.name || 'Unknown Asset',
		});
	};

	// Remove asset from reservable list after confirmation
	const confirmRemoveAsset = async () => {
		if (isSubmitting) return;

		try {
			setIsSubmitting(true);
			await onSetAssetReservable({
				assetId: removeAssetDialog.assetId,
				isReservable: false,
			});
			// Close dialog and refresh data
			setRemoveAssetDialog({
				open: false,
				assetId: '',
				assetName: '',
			});
			await refreshAssets();
		} catch (error) {
			console.error('Failed to remove asset from reservable list:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Open create group dialog
	const handleOpenCreateGroupDialog = () => {
		setCreateGroupDialog({
			open: true,
			name: '',
			description: '',
			selectedAssetIds: [],
		});
	};

	// Toggle asset selection in create group dialog
	const toggleGroupAssetSelection = (assetId) => {
		setCreateGroupDialog((prev) => ({
			...prev,
			selectedAssetIds: prev.selectedAssetIds.includes(assetId)
				? prev.selectedAssetIds.filter((id) => id !== assetId)
				: [...prev.selectedAssetIds, assetId],
		}));
	};

	// Create a new asset group
	const handleCreateGroup = async () => {
		if (isSubmitting || !createGroupDialog.name.trim()) return;

		try {
			setIsSubmitting(true);
			await onCreateAssetGroup({
				name: createGroupDialog.name.trim(),
				description: createGroupDialog.description.trim(),
				assetIds: createGroupDialog.selectedAssetIds,
			});
			// Close dialog and refresh data
			setCreateGroupDialog({
				open: false,
				name: '',
				description: '',
				selectedAssetIds: [],
			});
			await refreshAssetGroups();
		} catch (error) {
			console.error('Failed to create asset group:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Show confirmation dialog before deleting a group
	const handleDeleteGroup = (groupId) => {
		const group = assets.find((a) => a.groupId === groupId);
		setDeleteGroupDialog({
			open: true,
			groupId,
			groupName: group?.name || 'Unknown Group',
		});
	};

	// Delete group after confirmation
	const confirmDeleteGroup = async () => {
		if (isSubmitting) return;

		try {
			setIsSubmitting(true);
			await onDeleteAssetGroup({ groupId: deleteGroupDialog.groupId });
			// Close dialog and refresh data
			setDeleteGroupDialog({
				open: false,
				groupId: '',
				groupName: '',
			});
			await refreshAssetGroups();
		} catch (error) {
			console.error('Failed to delete asset group:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Open edit group dialog
	const handleEditGroup = (groupId) => {
		const group = assetGroupsData?.find((g) => g.id === groupId);
		if (!group) return;

		const currentAssetIds = (group.assets || []).map((a) => a.id);
		setEditGroupDialog({
			open: true,
			groupId: group.id,
			name: group.name || '',
			description: group.description || '',
			currentAssetIds,
			selectedAssetIds: [...currentAssetIds],
		});
	};

	// Toggle asset selection in edit group dialog
	const toggleEditGroupAssetSelection = (assetId) => {
		setEditGroupDialog((prev) => ({
			...prev,
			selectedAssetIds: prev.selectedAssetIds.includes(assetId)
				? prev.selectedAssetIds.filter((id) => id !== assetId)
				: [...prev.selectedAssetIds, assetId],
		}));
	};

	// Save group changes (rename and add/remove assets)
	const handleSaveGroupChanges = async () => {
		if (isSubmitting || !editGroupDialog.name.trim()) return;

		try {
			setIsSubmitting(true);

			const { groupId, name, description, currentAssetIds, selectedAssetIds } =
				editGroupDialog;

			// Update group name/description
			await onUpdateAssetGroup({
				groupId,
				name: name.trim(),
				description: description.trim(),
			});

			// Find assets to add and remove
			const assetsToAdd = selectedAssetIds.filter(
				(id) => !currentAssetIds.includes(id),
			);
			const assetsToRemove = currentAssetIds.filter(
				(id) => !selectedAssetIds.includes(id),
			);

			// Add new assets to group
			for (const assetId of assetsToAdd) {
				await onAddAssetToGroup({ groupId, assetId });
			}

			// Remove assets from group
			for (const assetId of assetsToRemove) {
				await onRemoveAssetFromGroup({ groupId, assetId });
			}

			// Close dialog and refresh data
			setEditGroupDialog({
				open: false,
				groupId: '',
				name: '',
				description: '',
				currentAssetIds: [],
				selectedAssetIds: [],
			});
			await refreshAssetGroups();
		} catch (error) {
			console.error('Failed to update asset group:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Check if data is loading
	const isLoading = !assetsData;

	return (
		<div className="bg-white">
			{/* Main Content */}
			<div className="bg-white rounded-xl border overflow-hidden shadow">
				<DateNavigation
					selectedDate={selectedDate}
					onDateChange={setSelectedDate}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					onZoomIn={zoomIn}
					onZoomOut={zoomOut}
					canZoomIn={canZoomIn}
					canZoomOut={canZoomOut}
				>
					<Button
						type="secondary"
						onClick={handleOpenAddAssetDialog}
						className="!py-1.5 !ml-2"
					>
						<PlusIcon className="h-4 w-4 mr-1" />
						Add Asset
					</Button>
					<Button
						type="secondary"
						onClick={handleOpenCreateGroupDialog}
						className="!py-1.5 !ml-2"
					>
						<FolderPlusIcon className="h-4 w-4 mr-1" />
						Create Group
					</Button>
				</DateNavigation>
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
						<span className="ml-3 text-gray-500">Loading assets...</span>
					</div>
				) : (
					<TimelineGrid
						assets={filteredAssets}
						selectedDate={selectedDate}
						viewMode={viewMode}
						onSlotRelease={handleSlotRelease}
						onSlotEdit={handleSlotEdit}
						onSlotView={handleSlotView}
						onSlotConfirm={handleSlotConfirm}
						onEmptyAreaClick={handleEmptyAreaClick}
						expandedGroups={expandedGroups}
						onToggleGroup={handleToggleGroup}
						onDateClick={(date) => {
							setSelectedDate(date);
							setViewMode('day');
						}}
						onRemoveAsset={handleRemoveAsset}
						onEditGroup={handleEditGroup}
						zoomLevel={zoomLevel}
						pendingBooking={addBookingModalState?.prefill}
					/>
				)}
			</div>
			{!isLoading && filteredAssets.length === 0 && (
				<div className="text-center py-12 bg-white rounded-xl border mt-4">
					<p className="text-gray-500">
						No assets found matching your criteria.
					</p>
				</div>
			)}

			{/* New Booking Modal */}
			<Modal
				open={newBookingDialog.open}
				onClose={() =>
					setNewBookingDialog((prev) => ({ ...prev, open: false }))
				}
				title={
					newBookingDialog.isGroup ? 'Create Group Booking' : 'Create Booking'
				}
				size="md"
				Footer={
					<>
						<Button
							onClick={handleCreateBooking}
							disabled={
								isSubmitting || newBookingDialog.selectedUserIds.length === 0
							}
						>
							{isSubmitting ? 'Creating...' : 'Create Booking'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setNewBookingDialog((prev) => ({ ...prev, open: false }))
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<div>
						<Label>{newBookingDialog.isGroup ? 'Group' : 'Asset'}</Label>
						<p className="text-sm font-medium text-gray-900">
							{newBookingDialog.assetName}
						</p>
						{newBookingDialog.isGroup && (
							<p className="text-xs text-gray-500 mt-1">
								All assets in this group will be reserved
							</p>
						)}
					</div>
					<div>
						<Label>Date</Label>
						<p className="text-sm font-medium text-gray-900">
							{format(newBookingDialog.date, 'EEEE, MMMM d, yyyy')}
						</p>
					</div>
					<div>
						<Label>Time Slot</Label>
						<p className="text-sm font-medium text-gray-900">
							{formatTimeFromMinutes(newBookingDialog.startMinutes)} -{' '}
							{formatTimeFromMinutes(newBookingDialog.endMinutes)}
						</p>
					</div>
					<div>
						<Label>Assign Users</Label>
						<div className="border rounded-md p-3 max-h-48 overflow-y-auto">
							{teamData?.rows?.map((user) => (
								<label
									key={user.id}
									className="flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
								>
									<input
										type="checkbox"
										checked={newBookingDialog.selectedUserIds.includes(user.id)}
										onChange={() => toggleUserSelection(user.id)}
										className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
									/>
									<span className="ml-3 text-sm text-gray-900">
										{getUserFullName(user)}
									</span>
									{user.email && (
										<span className="ml-2 text-xs text-gray-500">
											({user.email})
										</span>
									)}
								</label>
							))}
							{(!teamData?.rows || teamData.rows.length === 0) && (
								<p className="text-sm text-gray-500 py-2">
									No team members found
								</p>
							)}
						</div>
						{newBookingDialog.selectedUserIds.length === 0 && (
							<p className="text-xs text-amber-600 mt-1">
								Select at least one user for this booking
							</p>
						)}
					</div>
				</div>
			</Modal>

			{/* Release Confirmation Modal */}
			<Modal
				open={releaseDialog.open}
				onClose={() =>
					setReleaseDialog({
						open: false,
						slotId: '',
						assetId: '',
						assetName: '',
						bookedBy: '',
						isGroupBlocked: false,
					})
				}
				title={
					releaseDialog.isGroupBlocked
						? 'Remove Asset from Booking'
						: 'Release Booking'
				}
				size="sm"
				Footer={
					<>
						<Button
							type="danger"
							onClick={confirmRelease}
							disabled={isSubmitting}
						>
							{isSubmitting
								? 'Processing...'
								: releaseDialog.isGroupBlocked
									? 'Remove Asset'
									: 'Release Booking'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setReleaseDialog({
									open: false,
									slotId: '',
									assetId: '',
									assetName: '',
									bookedBy: '',
									isGroupBlocked: false,
								})
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-3">
					<p className="text-sm text-gray-600">
						{releaseDialog.isGroupBlocked
							? 'Are you sure you want to remove this asset from the group booking? The other assets in the group will remain booked.'
							: 'Are you sure you want to release this booking? This action cannot be undone.'}
					</p>
					<div className="bg-gray-50 rounded-md p-3">
						<p className="text-sm">
							<span className="font-medium text-gray-700">Asset:</span>{' '}
							<span className="text-gray-900">{releaseDialog.assetName}</span>
						</p>
						<p className="text-sm mt-1">
							<span className="font-medium text-gray-700">Booked by:</span>{' '}
							<span className="text-gray-900">{releaseDialog.bookedBy}</span>
						</p>
					</div>
				</div>
			</Modal>

			{/* Add Asset Modal */}
			<Modal
				open={addAssetDialog.open}
				onClose={() => setAddAssetDialog({ open: false, selectedAssetIds: [] })}
				title="Add Assets to Booking List"
				size="md"
				Footer={
					<>
						<Button
							onClick={handleAddAssetsToReservable}
							disabled={
								isSubmitting || addAssetDialog.selectedAssetIds.length === 0
							}
						>
							{isSubmitting ? 'Adding...' : 'Add Selected Assets'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setAddAssetDialog({ open: false, selectedAssetIds: [] })
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-600">
						Select assets to make available for booking. These assets will
						appear in the timeline view.
					</p>
					{nonReservableAssetsData && nonReservableAssetsData.length > 0 ? (
						<AssetMultiSelect
							assets={nonReservableAssetsData}
							value={addAssetDialog.selectedAssetIds}
							onChange={(selectedIds) =>
								setAddAssetDialog((prev) => ({
									...prev,
									selectedAssetIds: selectedIds,
								}))
							}
							placeholder="Search and select assets..."
						/>
					) : (
						<p className="text-sm text-gray-500 py-2">
							No assets available to add. All assets are already in the
							booking list.
						</p>
					)}
				</div>
			</Modal>

			{/* Remove Asset Confirmation Modal */}
			<Modal
				open={removeAssetDialog.open}
				onClose={() =>
					setRemoveAssetDialog({ open: false, assetId: '', assetName: '' })
				}
				title="Remove Asset from Booking List"
				size="sm"
				Footer={
					<>
						<Button
							type="danger"
							onClick={confirmRemoveAsset}
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Removing...' : 'Remove Asset'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setRemoveAssetDialog({
									open: false,
									assetId: '',
									assetName: '',
								})
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-3">
					<p className="text-sm text-gray-600">
						Are you sure you want to remove this asset from the booking list?
						The asset will no longer be available for booking.
					</p>
					<div className="bg-gray-50 rounded-md p-3">
						<p className="text-sm">
							<span className="font-medium text-gray-700">Asset:</span>{' '}
							<span className="text-gray-900">
								{removeAssetDialog.assetName}
							</span>
						</p>
					</div>
				</div>
			</Modal>

			{/* Create Group Modal */}
			<Modal
				open={createGroupDialog.open}
				onClose={() =>
					setCreateGroupDialog({
						open: false,
						name: '',
						description: '',
						selectedAssetIds: [],
					})
				}
				title="Create Asset Group"
				size="md"
				Footer={
					<>
						<Button
							onClick={handleCreateGroup}
							disabled={
								isSubmitting ||
								!createGroupDialog.name.trim() ||
								createGroupDialog.selectedAssetIds.length === 0
							}
						>
							{isSubmitting ? 'Creating...' : 'Create Group'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setCreateGroupDialog({
									open: false,
									name: '',
									description: '',
									selectedAssetIds: [],
								})
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<div>
						<Label>Group Name</Label>
						<TextField
							value={createGroupDialog.name}
							onChange={(e) =>
								setCreateGroupDialog((prev) => ({
									...prev,
									name: e.target.value,
								}))
							}
							placeholder="Enter group name"
						/>
					</div>
					<div>
						<Label>Description (optional)</Label>
						<textarea
							value={createGroupDialog.description}
							onChange={(e) =>
								setCreateGroupDialog((prev) => ({
									...prev,
									description: e.target.value,
								}))
							}
							placeholder="Enter group description"
							rows={2}
							className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
						/>
					</div>
					<div>
						<Label>Select Assets</Label>
						<p className="text-sm text-gray-600 mb-2">
							Choose assets to include in this group.
						</p>
						<div className="border rounded-md p-3 max-h-48 overflow-y-auto">
							{assetsData?.map((asset) => (
								<label
									key={asset.id}
									className="flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
								>
									<input
										type="checkbox"
										checked={createGroupDialog.selectedAssetIds.includes(
											asset.id,
										)}
										onChange={() => toggleGroupAssetSelection(asset.id)}
										className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
									/>
									<span className="ml-3 text-sm text-gray-900">
										{getStandardAssetName(asset)}
									</span>
									{asset.serialNumber && (
										<span className="ml-2 text-xs text-gray-500">
											({asset.serialNumber})
										</span>
									)}
								</label>
							))}
							{(!assetsData || assetsData.length === 0) && (
								<p className="text-sm text-gray-500 py-2">
									No reservable assets available. Add assets to the booking list
									first.
								</p>
							)}
						</div>
						{createGroupDialog.selectedAssetIds.length > 0 && (
							<p className="text-xs text-gray-600 mt-1">
								{createGroupDialog.selectedAssetIds.length} asset(s) selected
							</p>
						)}
					</div>
				</div>
			</Modal>

			{/* Delete Group Confirmation Modal */}
			<Modal
				open={deleteGroupDialog.open}
				onClose={() =>
					setDeleteGroupDialog({ open: false, groupId: '', groupName: '' })
				}
				title="Delete Asset Group"
				size="sm"
				Footer={
					<>
						<Button
							type="danger"
							onClick={confirmDeleteGroup}
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Deleting...' : 'Delete Group'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setDeleteGroupDialog({
									open: false,
									groupId: '',
									groupName: '',
								})
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-3">
					<p className="text-sm text-gray-600">
						Are you sure you want to delete this group? The assets in this group
						will remain available for booking individually.
					</p>
					<div className="bg-gray-50 rounded-md p-3">
						<p className="text-sm">
							<span className="font-medium text-gray-700">Group:</span>{' '}
							<span className="text-gray-900">
								{deleteGroupDialog.groupName}
							</span>
						</p>
					</div>
				</div>
			</Modal>

			{/* Edit Group Modal */}
			<Modal
				open={editGroupDialog.open}
				onClose={() =>
					setEditGroupDialog({
						open: false,
						groupId: '',
						name: '',
						description: '',
						currentAssetIds: [],
						selectedAssetIds: [],
					})
				}
				title="Edit Asset Group"
				size="md"
				Footer={
					<>
						<Button
							onClick={handleSaveGroupChanges}
							disabled={
								isSubmitting ||
								!editGroupDialog.name.trim() ||
								editGroupDialog.selectedAssetIds.length === 0
							}
						>
							{isSubmitting ? 'Saving...' : 'Save Changes'}
						</Button>
						<Button
							type="secondary"
							onClick={() =>
								setEditGroupDialog({
									open: false,
									groupId: '',
									name: '',
									description: '',
									currentAssetIds: [],
									selectedAssetIds: [],
								})
							}
						>
							Cancel
						</Button>
					</>
				}
			>
				<div className="space-y-4">
					<div>
						<Label>Group Name</Label>
						<TextField
							value={editGroupDialog.name}
							onChange={(e) =>
								setEditGroupDialog((prev) => ({
									...prev,
									name: e.target.value,
								}))
							}
							placeholder="Enter group name"
						/>
					</div>
					<div>
						<Label>Description (optional)</Label>
						<textarea
							value={editGroupDialog.description}
							onChange={(e) =>
								setEditGroupDialog((prev) => ({
									...prev,
									description: e.target.value,
								}))
							}
							placeholder="Enter group description"
							rows={2}
							className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
						/>
					</div>
					<div>
						<Label>Assets in Group</Label>
						<p className="text-sm text-gray-600 mb-2">
							Select which assets should be in this group.
						</p>
						<div className="border rounded-md p-3 max-h-48 overflow-y-auto">
							{assetsData?.map((asset) => {
								const isInGroup = editGroupDialog.selectedAssetIds.includes(
									asset.id,
								);
								const wasInGroup = editGroupDialog.currentAssetIds.includes(
									asset.id,
								);
								return (
									<label
										key={asset.id}
										className="flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
									>
										<input
											type="checkbox"
											checked={isInGroup}
											onChange={() => toggleEditGroupAssetSelection(asset.id)}
											className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
										/>
										<span className="ml-3 text-sm text-gray-900">
											{getStandardAssetName(asset)}
										</span>
										{asset.serialNumber && (
											<span className="ml-2 text-xs text-gray-500">
												({asset.serialNumber})
											</span>
										)}
										{/* Show badge for added/removed status */}
										{isInGroup && !wasInGroup && (
											<span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
												Adding
											</span>
										)}
										{!isInGroup && wasInGroup && (
											<span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
												Removing
											</span>
										)}
									</label>
								);
							})}
							{(!assetsData || assetsData.length === 0) && (
								<p className="text-sm text-gray-500 py-2">
									No reservable assets available.
								</p>
							)}
						</div>
						{editGroupDialog.selectedAssetIds.length > 0 && (
							<p className="text-xs text-gray-600 mt-1">
								{editGroupDialog.selectedAssetIds.length} asset(s) in group
							</p>
						)}
					</div>
				</div>
			</Modal>
		</div>
	);
}
